import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import {
  AccreditationsCTA,
  BgImage,
  FloatingSection,
  Header,
  Intro,
  NewsItems,
  NewsWidget,
  SmartLink,
} from "@prmf/site-components";
import { Post } from "@prmf/site-data";
import { routes, t } from "@prmf/site-router";
import { useLoaderData } from "@remix-run/react";

const steps = [
  {
    title: "Curiosity",
    points: [
      "Questioning the way things are.",
      "Seeking out new ideas.",
      "Experimenting with new approaches.",
      "Sharing learnings.",
    ],
  },
  {
    title: "Partnership",
    points: [
      "Getting to know each other.",
      "Seeking first to understand.",
      "Collaborating to improve and grow.",
      "Delivering creative solutions that add value and manage risk.",
    ],
  },
  {
    title: "Quality",
    points: [
      "Delivering on our commitments.",
      "Focusing on right first time in all our activities.",
      "Keeping to our processes & policies.",
      "Clarifying & communicating expectations.",
    ],
  },
  {
    title: "Respect",
    points: [
      "Being honest & open.",
      "Treating everyone as a valued colleague.",
      "Supporting each other to deliver for the customer.",
      "Giving effective feedback.",
    ],
  },
  {
    title: "Sustainability",
    points: [
      "Taking decisions that show we value all our resources.",
      "Minimising the environmental impact of our activities.",
      "Speaking up for sustainable choices for the planet and stakeholders.",
      "Contributing to our CSR initiatives and/or those of our partners.",
    ],
  },
];

export default function WorkingWithUs() {
  const data = useLoaderData<{ posts: Post[] }>();
  return (
    <div>
      <Header
        title={t(routes.workingWithUs, "title")}
        image={t(routes.workingWithUs, "image")}
      >
        <p>
          We believe in fostering a work environment where success is not just
          measured by quarterly and annual awards, but by the everyday
          victories, big and small. We understand the importance of
          acknowledging individual contributions, which is why we make a point
          of honouring them regularly.{" "}
        </p>
        <p>
          Whether it’s hitting a target, coming up with an innovative solution,
          or going above and beyond for a colleague, we ensure that every
          accomplishment is celebrated.
        </p>
      </Header>
      <Intro title="A growing and dynamic team">
        <p>
          But it’s not all about work here. We firmly believe in the power of
          fun and camaraderie. That’s why we organise regular team-building
          activities and encourage healthy competition among our teams. From
          friendly challenges to exciting events, there’s always something fun
          happening around the office. We know that when people enjoy coming to
          work, they feel connected to their colleagues, are happier and more
          motivated.
        </p>
      </Intro>
      <div className="mt-10 space-y-20">
        <FloatingSection
          bgColor="white"
          image="/media/assets/working-with-us-item-1.jpg"
          imageClassName="bg-top"
          reverse
          title={
            <>
              <h3 className="font-semibold">
                Our culture is one of collaboration, teamwork, pride, and fun!
              </h3>
              <p>
                We believe in listening to our employees and understand that
                your success is built upon the hard work and dedication of our
                team. We are committed to providing a supportive and inclusive
                work environment, where everyone can thrive and reach their full
                potential. That’s why we focus on providing continuous two-way
                communication.
              </p>
              <p>
                Working hard to create a supportive and inclusive environment
                through vertical dialogue and cross-functional communication, we
                actively seek feedback from employees and respond to ideas so
                that everyone has a chance to obtain special recognition.
              </p>
            </>
          }
        />
        <FloatingSection
          className="min-h-[380px]"
          image="/media/assets/working-with-us-item-2.jpg"
          title={
            <>
              <h3 className="font-semibold">Who we are</h3>
              <p>
                Based in Sussex on the south coast, we are a leading
                manufacturer of high-quality fitted furniture, supplying
                products to residential projects throughout the UK. We are
                passionate about bringing sustainability and pride into homes by
                creating beautiful and functional spaces for our customers. We
                pride ourselves on our exceptional customer experience,
                innovative designs and commitment to sustainability.
              </p>
            </>
          }
        />
        <FloatingSection
          reverse
          className="min-h-[380px]"
          bgColor="#FFFFFF"
          image="/media/assets/working-with-us-gptw.webp"
          imageClassName="bg-[length:auto_80%] bg-white"
          title={
            <>
              <h3 className="font-semibold">We are a great place to work!</h3>
              <p>
                We manufacture, supply, and install beautiful, environmentally
                sustainable kitchens, bedroom, and bathroom furniture across the
                UK. Proudly based in West Sussex, we employ people who live and
                breathe our values: quality, partnership, curiosity,
                sustainability, and above all, respect.
              </p>
              <p>
                As part of the Ballingslöv Group, we blend the warmth of a small
                company with the strength of a wider European family. We’re
                ambitious – working hard, aiming high, and partnering with
                like-minded customers who value integrity and collaboration. We
                never lose sight of what matters: our people, our community, our
                planet, having fun, and enjoying the journey together.
              </p>
              <SmartLink
                className="button ml-0 w-max border border-gray-700 bg-transparent text-gray-700"
                to="https://www.greatplacetowork.co.uk/certified-company/1573860"
              >
                <span>View Profile</span>
                <ArrowTopRightOnSquareIcon className="size-4" />
              </SmartLink>
            </>
          }
        />
        <FloatingSection
          className="min-h-[380px]"
          image="/media/assets/working-with-us-nbs.png"
          imageClassName="bg-[length:auto_80%]"
          title={
            <>
              <h3 className="font-semibold">Partnership with NBS</h3>
              <p>
                At Paula Rosa Manhattan, we are proud to announce our
                collaborative venture with the National Building Specification
                (NBS), bringing a new level of excellence and innovation to the
                UK construction industry. As renowned kitchen manufacturers, we
                are committed to delivering quality, functionality, and style.
                Our comprehensive range of kitchen products, now featured on
                NBS, underscores this commitment, providing architects,
                designers, and builders with immediate access to top-tier
                kitchen solutions.
              </p>
              <p>
                Our presence on NBS is more than just a listing. It's a
                comprehensive solution to streamline the specification process
                for our clients. With detailed information and resources readily
                available, selecting and specifying the ideal kitchen components
                becomes a more efficient and hassle-free experience.
              </p>
              <p>
                Our partnership with NBS represents Paula Rosa Manhattan's
                commitment to leading the way in kitchen design and innovation.
                We are dedicated to providing solutions that blend aesthetics
                with functionality, backed by the assurance of quality and
                compliance. Discover the Paula Rosa Manhattan difference on NBS
                today – where visionary design meets practicality.
              </p>
              <SmartLink
                className="button ml-0 w-max border border-gray-700 bg-transparent text-gray-700"
                to="https://source.thenbs.com/manufacturer/paula-rosa-manhattan/kE5F4zWnzn6V9QXWA8fnxK/overview"
              >
                <span>View Profile</span>
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </SmartLink>
            </>
          }
        />
        <div className="container grid gap-10 md:grid-cols-2">
          <section>
            <BgImage
              className="aspect-video bg-top"
              src="/media/assets/working-with-us-item-3.jpg"
            />
            <div className="relative mx-4 -mt-10 bg-white p-8 text-sm">
              <h3 className="font-semibold">Our values</h3>
              <p>
                At the heart of our company are our core values. We strive for
                excellence in everything we do, constantly seeking new and
                better ways to improve our products and processes. We are also
                committed to preserving the environment for future generations,
                using sustainable materials and practices in our manufacturing
                processes.
              </p>
            </div>
          </section>
          <section>
            <BgImage
              className="aspect-video bg-top"
              src="/media/assets/working-with-us-item-4.jpg"
            />
            <div className="relative mx-4 -mt-10 bg-white p-8 text-sm">
              <h3 className="font-semibold">
                Valuing diversity and providing equity and inclusion in
                everything we do
              </h3>
              <p>
                We recognise the benefits of diversity, equity and inclusion. We
                are committed to creating and maintaining an inclusive culture,
                where all employees can contribute wholly, allowing them, as
                individuals and teams, to reach their full potential. Paula Rosa
                Manhattan pursues the same values as a business.
              </p>
            </div>
          </section>
        </div>
      </div>
      <div className="container my-20 grid gap-10 md:grid-cols-3 lg:grid-cols-5">
        {steps.map(({ title, points }, idx) => (
          <div key={title} className="space-y-4 text-xs">
            <BgImage
              className="aspect-square rounded-lg bg-black shadow-lg"
              src={`/media/assets/working-with-us-step-${idx + 1}.png`}
              title={title}
            />
            <h3 className="sr-only">{title}</h3>
            <ul className="ml-4 list-disc space-y-2">
              {points.map((p, idx) => (
                <li key={`p-${idx}`}>{p}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <section
        className="bg-gray-100 p-10 text-center"
        id="available-positions"
      >
        <div className="container">
          <h2 className="mb-2 text-lg font-semibold">Available positions</h2>
          <p className="mx-auto max-w-3xl">
            We value dedication, creativity, craftsmanship and innovation.
            Working as one big family, whether from our head office in Lancing,
            or elsewhere around the UK, our team are essential to our success.
          </p>
          <NewsItems className="my-20 xl:!grid-cols-3" posts={data.posts} />
        </div>
      </section>
      <AccreditationsCTA />
      <NewsWidget />
    </div>
  );
}
